import { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, AudioLines, ShieldAlert, Navigation, ChevronLeft, ChevronRight, Smartphone } from "lucide-react";

const cards = [
  {
    img: "/products/ctx-child.jpg",
    title: "Know where they are",
    to: "/app",
    chip: { icon: MapPin, label: "Live location", sub: "Ethan · en route home" },
  },
  {
    img: "/products/ctx-teen.jpg",
    title: "Talk in a single tap",
    to: "/app",
    chip: { icon: AudioLines, label: "Two-way voice", sub: "Connected · crystal clear", eq: true },
  },
  {
    img: "/products/ctx-adults.jpg",
    title: "Help the instant it's needed",
    to: "/app",
    chip: { icon: ShieldAlert, label: "Emergency SOS", sub: "One tap to 911", alert: true },
  },
  {
    img: "/products/ctx-guide.jpg",
    title: "Guided safely home",
    to: "/app",
    chip: { icon: Navigation, label: "Take me home", sub: "Turn-by-turn guidance" },
  },
  {
    img: "/products/app-home.jpg",
    title: "Everything in one glance",
    to: "/app",
    ui: true,
    chip: { icon: Smartphone, label: "The Vsense app", sub: "Live view, voice & SOS" },
  },
  {
    img: "/products/app-map.jpg",
    title: "Live location & history",
    to: "/app",
    ui: true,
    chip: { icon: MapPin, label: "Location screen", sub: "Real-time map + history" },
  },
];

export const FeatureCards = () => {
  const navigate = useNavigate();
  const scroller = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const child = el.children[0];
    if (!child) return;
    const step = child.offsetWidth + 20; // card width + gap
    setActive(Math.round(el.scrollLeft / step));
  }, []);

  const goTo = (i) => {
    const el = scroller.current;
    if (!el) return;
    const child = el.children[i];
    if (child) el.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
  };

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const child = el.children[0];
    const step = child ? child.offsetWidth + 20 : 340;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section data-testid="feature-cards" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">One frame. Every safeguard.</p>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-tight">
            Built to look out for<br className="hidden sm:block" /> the ones you love.
          </h2>
          <p className="mt-4 text-base md:text-lg text-zinc-500">
            Real-time safety, woven into a pair of glasses they'll actually want to wear.
          </p>
        </div>

        {/* Cards row */}
        <div className="relative mt-12">
          <button
            data-testid="feature-cards-prev"
            onClick={() => scrollBy(-1)}
            aria-label="Previous cards"
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-11 h-11 rounded-full bg-white text-zinc-900 items-center justify-center shadow-[0_8px_24px_rgb(0,0,0,0.15)] hover:bg-zinc-100 active:scale-95 transition-all duration-200 disabled:opacity-40"
            disabled={active === 0}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="feature-cards-next"
            onClick={() => scrollBy(1)}
            aria-label="Next cards"
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-11 h-11 rounded-full bg-white text-zinc-900 items-center justify-center shadow-[0_8px_24px_rgb(0,0,0,0.15)] hover:bg-zinc-100 active:scale-95 transition-all duration-200 disabled:opacity-40"
            disabled={active >= cards.length - 1}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={scroller}
            onScroll={onScroll}
            className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-6 px-6 md:mx-0 md:px-0"
          >
          {cards.map((c, i) => {
            const Icon = c.chip.icon;
            return (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className={`group relative snap-start shrink-0 w-[78vw] sm:w-[320px] lg:w-[360px] aspect-[3/4] rounded-3xl overflow-hidden shadow-[0_16px_40px_rgb(0,0,0,0.10)] ${c.ui ? "bg-gradient-to-b from-[#efeff1] to-white" : "bg-zinc-200"}`}
              >
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {c.ui ? (
                  <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-white/85 to-transparent" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/55" />
                )}

                {/* Title */}
                <h3 className={`absolute top-6 left-6 right-6 font-display text-2xl md:text-[1.7rem] font-extrabold tracking-tight leading-tight ${c.ui ? "text-zinc-900" : "text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]"}`}>
                  {c.title}
                </h3>

                {/* Data chip */}
                <div className="absolute bottom-6 left-6 right-16">
                  <div className="inline-flex items-center gap-2.5 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 px-3 py-2 shadow-lg">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${c.chip.alert ? "bg-red-600 text-white" : "bg-zinc-900 text-white"}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-semibold text-zinc-900 truncate">{c.chip.label}</span>
                        {c.chip.eq && (
                          <span className="flex items-end gap-[2px] h-3">
                            {[0, 0.15, 0.3, 0.2].map((d, k) => (
                              <span key={k} className="eq-bar bg-zinc-900" style={{ animationDelay: `${d}s` }} />
                            ))}
                          </span>
                        )}
                        {c.chip.alert && (
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500/60" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600" />
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">{c.chip.sub}</div>
                    </div>
                  </div>
                </div>

                {/* Plus button */}
                <button
                  data-testid={`feature-card-plus-${i}`}
                  onClick={() => navigate(c.to)}
                  aria-label={`Learn about ${c.title}`}
                  className="absolute bottom-6 right-6 w-10 h-10 rounded-full bg-zinc-900 text-white flex items-center justify-center shadow-lg hover:bg-zinc-800 active:scale-95 transition-all duration-200"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            );
          })}
          </div>
        </div>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-2">
          {cards.map((c, i) => (
            <button
              key={i}
              data-testid={`feature-cards-dot-${i}`}
              onClick={() => goTo(i)}
              aria-label={`Go to card ${i + 1}`}
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: i === active ? 24 : 8, backgroundColor: i === active ? "#18181b" : "#d4d4d8" }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
